import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import Logo from "@/components/Logo";
import NotificationBell from "@/components/NotificationBell";
import CurrencyPicker from "@/components/CurrencyPicker";
import { Search, Plus, MessageCircle, LogOut, LayoutDashboard, User as UserIcon, Menu, X, ShieldCheck, BadgeCheck, Crown, Map as MapIcon, LifeBuoy, MapPin, Navigation, Banknote, Hammer } from "lucide-react";

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);

  useEffect(() => {
    if (!user) { setUnreadMessages(0); return; }
    let alive = true;
    const fetchUnread = async () => {
      try {
        const r = await api.get("/conversations");
        if (!alive) return;
        const total = (r.data || []).reduce((s, c) => s + (c.unread_count || 0), 0);
        setUnreadMessages(total);
      } catch (_) { /* ignore */ }
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [user]);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/browse?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-black/5">
      <div className="container-narrow flex items-center gap-6 h-16">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 shrink-0 group">
          <Logo size={44} withWordmark wordmarkClassName="text-2xl"/>
        </Link>

        <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-xl relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="nav-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search products, services, countries…"
            className="pl-9 bg-[#F3EFE9] border-transparent focus-visible:bg-white"
          />
        </form>

        <nav className="hidden md:flex items-center gap-1">
          <NavLink to="/browse?type=product" data-testid="nav-products" className={({isActive}) => `px-3 py-2 text-sm font-medium rounded-md hover:bg-black/5 ${isActive ? "text-[#C05A3C]" : ""}`}>Products</NavLink>
          <NavLink to="/browse?type=service" data-testid="nav-services" className={({isActive}) => `px-3 py-2 text-sm font-medium rounded-md hover:bg-black/5 ${isActive ? "text-[#C05A3C]" : ""}`}>Services</NavLink>
          <NavLink to="/locations" data-testid="nav-locations" className={({isActive}) => `px-3 py-2 text-sm font-medium rounded-md hover:bg-black/5 flex items-center gap-1 ${isActive ? "text-[#C05A3C]" : ""}`}>
            <MapPin className="w-3.5 h-3.5"/> Locations
          </NavLink>
          <NavLink to="/near-me" data-testid="nav-near-me" className={({isActive}) => `px-3 py-2 text-sm font-medium rounded-md hover:bg-black/5 flex items-center gap-1 ${isActive ? "text-[#C05A3C]" : ""}`}>
            <Navigation className="w-3.5 h-3.5"/> Near me
          </NavLink>
          <NavLink to="/map" data-testid="nav-map" className={({isActive}) => `px-3 py-2 text-sm font-medium rounded-md hover:bg-black/5 flex items-center gap-1 ${isActive ? "text-[#C05A3C]" : ""}`}>
            <MapIcon className="w-3.5 h-3.5"/> Map
          </NavLink>
          <NavLink to="/makers" data-testid="nav-makers" className={({isActive}) => `px-3 py-2 text-sm font-medium rounded-md hover:bg-black/5 flex items-center gap-1 ${isActive ? "text-[#C05A3C]" : ""}`}>
            <Hammer className="w-3.5 h-3.5"/> Makers
          </NavLink>
          <NavLink to="/pro" data-testid="nav-pro" className={({isActive}) => `px-3 py-2 text-sm font-medium rounded-md hover:bg-black/5 flex items-center gap-1 ${isActive ? "text-[#D9923B]" : "text-[#D9923B]"}`}>
            <Crown className="w-3.5 h-3.5"/> Pro
          </NavLink>
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <CurrencyPicker className="hidden sm:inline-flex" />
          {user ? (
            <>
              <Button data-testid="nav-sell-btn" onClick={() => navigate(user.verification_status === "approved" ? "/create" : "/verify")} className="bg-[#C05A3C] hover:bg-[#D46B4E] text-white rounded-md hidden sm:inline-flex">
                <Plus className="w-4 h-4 mr-1" /> Sell
              </Button>
              <Button data-testid="nav-messages-btn" variant="ghost" size="icon" onClick={() => navigate("/dashboard/messages")} className="rounded-full relative">
                <MessageCircle className="w-5 h-5" />
                {unreadMessages > 0 && (
                  <span data-testid="nav-messages-unread" className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#C05A3C] text-white text-[10px] font-semibold flex items-center justify-center">
                    {unreadMessages > 9 ? "9+" : unreadMessages}
                  </span>
                )}
              </Button>
              <NotificationBell />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="nav-user-menu" className="rounded-full focus:outline-none focus:ring-2 focus:ring-[#C05A3C]/50">
                    <Avatar className="w-9 h-9 border border-black/10">
                      <AvatarImage src={user.picture || undefined} />
                      <AvatarFallback className="bg-[#233348] text-white text-sm">
                        {user.name?.slice(0, 1).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-sans-ui">
                    <div className="text-sm font-semibold truncate flex items-center gap-1">
                      {user.name}
                      {user.verified_badge && <BadgeCheck className="w-4 h-4 text-[#C05A3C]"/>}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                    <div className="flex gap-1 mt-1">
                      {user.is_pro && <Badge className="bg-[#D9923B] text-[#1A2024] text-[10px]"><Crown className="w-2.5 h-2.5 mr-0.5"/> PRO</Badge>}
                      {user.role === "admin" && <Badge className="bg-[#1A2024] text-white text-[10px]">ADMIN</Badge>}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard"><LayoutDashboard className="w-4 h-4 mr-2"/> Dashboard</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/dashboard/payouts")} data-testid="menu-payouts"><Banknote className="w-4 h-4 mr-2"/> Payouts · Stripe</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/dashboard/messages")} data-testid="menu-messages"><MessageCircle className="w-4 h-4 mr-2"/> Messages</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/verify")} data-testid="menu-verify"><ShieldCheck className="w-4 h-4 mr-2"/> Verification</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/pro")} data-testid="menu-pro" className={user.is_pro ? "" : "text-[#D9923B]"}><Crown className="w-4 h-4 mr-2"/> {user.is_pro ? "Manage Pro" : "Upgrade to Pro"}</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/create")} data-testid="menu-create"><Plus className="w-4 h-4 mr-2"/> New listing</DropdownMenuItem>
                  {user.role === "admin" && <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin"><UserIcon className="w-4 h-4 mr-2"/> Admin panel</DropdownMenuItem>}
                  {user.role === "admin" && <DropdownMenuItem onClick={() => navigate("/admin/complaints")} data-testid="menu-admin-complaints"><LifeBuoy className="w-4 h-4 mr-2"/> Admin · Cases</DropdownMenuItem>}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout"><LogOut className="w-4 h-4 mr-2"/> Log out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Button data-testid="nav-login-btn" variant="ghost" onClick={() => navigate("/login")} className="hidden sm:inline-flex">Log in</Button>
              <Button data-testid="nav-signup-btn" onClick={() => navigate("/register")} className="bg-[#C05A3C] hover:bg-[#D46B4E] text-white rounded-md">Join AfriMarket</Button>
            </>
          )}
          <button className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="nav-mobile-toggle">
            {mobileOpen ? <X className="w-5 h-5"/> : <Menu className="w-5 h-5"/>}
          </button>
        </div>
      </div>
      {mobileOpen && (
        <div className="md:hidden border-t border-black/5 bg-white px-4 py-3 space-y-2">
          <form onSubmit={submitSearch} className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-9 bg-[#F3EFE9] border-transparent"/>
          </form>
          <button onClick={() => { setMobileOpen(false); navigate("/browse?type=product"); }} className="block w-full text-left py-2 text-sm">Products</button>
          <button onClick={() => { setMobileOpen(false); navigate("/browse?type=service"); }} className="block w-full text-left py-2 text-sm">Services</button>
          <button onClick={() => { setMobileOpen(false); navigate("/locations"); }} className="block w-full text-left py-2 text-sm">Shop by location</button>
          <button onClick={() => { setMobileOpen(false); navigate("/near-me"); }} className="block w-full text-left py-2 text-sm">Near me</button>
          <button onClick={() => { setMobileOpen(false); navigate("/map"); }} className="block w-full text-left py-2 text-sm">Map view</button>
          {user && (
            <button onClick={() => { setMobileOpen(false); navigate("/create"); }} className="block w-full text-left py-2 text-sm text-[#C05A3C] font-semibold">+ Sell something</button>
          )}
          <div className="pt-2 border-t border-black/5">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Display currency</div>
            <CurrencyPicker className="w-full justify-start" />
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
